// OnPlan.Web/OnPlan.Frontend/src/app/services/feedback.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ToastService } from './toasts.service';
import * as requests from '../requests';
import { Feedback, FeedbackAttachment } from '../models/feedback';

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private apiUrl = `${environment.apiUrl}/feedback`;

  constructor(private http: HttpClient, private toastService: ToastService) {}

  private handleError(error: HttpErrorResponse, message: string) {
    console.error('Feedback API error:', error);
    this.toastService.show(message, {
      classname: 'bg-danger text-light p-3 fs-5',
      delay: 5000,
    });
    return throwError(() => error);
  }

  /** Create feedback (no attachments) */
  createFeedback(request: requests.FeedbackCreateRequest): Observable<Feedback | null> {
    return this.http.post<Feedback>(`${this.apiUrl}/create`, request, {
      withCredentials: true,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    }).pipe(
      map(res => res),
      catchError((error: HttpErrorResponse) => {
        const backendMessage = error.error && typeof error.error === 'string'
          ? error.error
          : (error.message || 'Failed to create feedback');
        this.toastService.show(backendMessage, { classname: 'bg-danger text-light p-3 fs-5' });
        return of(null);
      })
    );
  }

  /** Upload single attachment with progress */
  uploadAttachment(feedbackId: string, file: File): Observable<number | FeedbackAttachment> {
  const formData = new FormData();
  formData.append('file', file);

  return this.http.post<FeedbackAttachment>(`${this.apiUrl}/${feedbackId}/attachment/add`, formData, {
    withCredentials: true,
    reportProgress: true,
    observe: 'events',
  }).pipe(
    map((event: HttpEvent<any>) => {
      switch (event.type) {
        case HttpEventType.UploadProgress:
          return Math.round((100 * (event.loaded || 0)) / (event.total || 1));
        case HttpEventType.Response:
          return event.body as FeedbackAttachment;
        default:
          return 0;
      }
    }),
    catchError(() => {
      this.toastService.show(`Failed to upload ${file.name}`, { classname: 'bg-danger text-light p-3 fs-5' });
      return of(0);
    })
  );
}

  /** Delete single attachment */
  deleteAttachment(attachmentId: string): Observable<boolean> {
    return this.http.delete(`${this.apiUrl}/attachment/${attachmentId}/delete`, {
      withCredentials: true,
    }).pipe(
      map(() => true),
      catchError(() => {
        this.toastService.show('Failed to delete attachment', { classname: 'bg-danger text-light p-3 fs-5' });
        return of(false);
      })
    );
  }

  /** Get feedback for a planogram */
  getFeedback(request: requests.GetFeedbackRequest): Observable<Feedback[]> {
    return this.http.post<Feedback[]>(`${this.apiUrl}/retrieve`, request, {
      withCredentials: true,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    }).pipe(
      map((res) => res || []),
      catchError(() => {
        this.toastService.show('Failed to retrieve feedback', { classname: 'bg-danger text-light p-3 fs-5' });
        return of([]);
      })
    );
  }

  /** Delete feedback item */
  deleteFeedback(feedbackId: string): Observable<boolean> {
    return this.http.delete(`${this.apiUrl}/${feedbackId}`, {
      withCredentials: true,
    }).pipe(
      map(() => true),
      catchError(() => {
        this.toastService.show('Failed to delete feedback', { classname: 'bg-danger text-light p-3 fs-5' });
        return of(false);
      })
    );
  }
}